import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { setItemTotalQuantity } from './useBatchOps'

export type AdjustReason = 'used' | 'bought' | 'correction' | 'expired'

type AdjustInput = {
  itemId:      string
  householdId: string
  oldTotal:    number   // current quantity BEFORE the change
  newTotal:    number   // target quantity (clamped >= 0)
  delta:       number   // newTotal - oldTotal, used for toast text
  reason:      AdjustReason
}

// Writes through batches as the canonical inventory model.
//   - Increment → adds a new batch for the difference
//   - Decrement → consumes oldest-expiry batches first (FIFO)
//   - The DB trigger maintains pantry_items.current_quantity in sync.
// Always appends a row to quantity_adjustments for the audit trail.
export function useAdjustQuantity() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (input: AdjustInput) => {
      const now          = new Date().toISOString()
      const safeNewTotal = Math.max(0, input.newTotal)

      // 1. Apply the change through batches. The trigger updates
      //    pantry_items.current_quantity automatically.
      await setItemTotalQuantity({
        itemId:      input.itemId,
        householdId: input.householdId,
        newTotal:    safeNewTotal,
      })

      // 2. Append the audit log. Failure here is non-fatal — the inventory
      //    state is already correct via the trigger; missing audit rows just
      //    mean the analytics surface won't have this event.
      const { error: logErr } = await supabase
        .from('quantity_adjustments')
        .insert({
          item_id:           input.itemId,
          household_id:      input.householdId,
          previous_quantity: input.oldTotal,
          new_quantity:      safeNewTotal,
          reason:            input.reason,
          adjusted_at:       now,
        })
      if (logErr) {
        console.warn('[useAdjustQuantity] audit log insert failed:', logErr)
      }

      return { itemId: input.itemId, newTotal: safeNewTotal }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['pantry-items', vars.householdId] })
      qc.invalidateQueries({ queryKey: ['home',         vars.householdId] })
    },
  })
}
